/**
 * Soft-keyboard inset via visualViewport (G18).
 * Publishes `--customer-keyboard-inset` on documentElement so browse/search
 * chrome can pad above the keyboard without relying on innerWidth stubs alone.
 */
import { useEffect, useState } from 'react';

export const CUSTOMER_KEYBOARD_INSET_CSS_VAR = '--customer-keyboard-inset' as const;

function readKeyboardInsetPx(): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const vv = window.visualViewport;
  if (vv === null || vv === undefined) {
    return 0;
  }
  const inset = Math.round(window.innerHeight - vv.height - vv.offsetTop);
  return Number.isFinite(inset) ? Math.max(0, inset) : 0;
}

function publishKeyboardInset(insetPx: number): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.style.setProperty(
    CUSTOMER_KEYBOARD_INSET_CSS_VAR,
    `${insetPx}px`,
  );
}

/**
 * Tracks soft-keyboard overlap using visualViewport resize/scroll.
 * Returns inset px; also mirrors to `--customer-keyboard-inset`.
 */
export function useVisualViewportKeyboardInset(): number {
  const [insetPx, setInsetPx] = useState(0);

  useEffect(() => {
    const apply = (): void => {
      const next = readKeyboardInsetPx();
      setInsetPx(next);
      publishKeyboardInset(next);
    };

    apply();

    const vv = window.visualViewport;
    if (vv === null || vv === undefined) {
      return () => {
        publishKeyboardInset(0);
      };
    }

    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);

    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      publishKeyboardInset(0);
    };
  }, []);

  return insetPx;
}
